import { useState, useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { trpc } from '../lib/trpc.js';
import { formatGbp, formatCpl } from '../lib/format.js';
import { clsx } from 'clsx';
import { format, subDays, subMonths, startOfYear } from 'date-fns';

type Granularity = 'day' | 'week' | 'month';

type MetricKey = 'revenue' | 'jobsScheduled' | 'jobsCompleted' | 'jobsCancelled' | 'spend' | 'leads' | 'adLeads' | 'cpl' | 'laborCost' | 'netProfit';

interface MetricConfig {
  key: MetricKey;
  label: string;
  color: string;
  type: 'bar' | 'line';
  format: (v: number) => string;
}

const METRICS: MetricConfig[] = [
  { key: 'revenue',       label: 'Revenue',          color: '#16a34a', type: 'bar',  format: formatGbp },
  { key: 'laborCost',     label: 'Labor cost',       color: '#7c2d12', type: 'bar',  format: formatGbp },
  { key: 'netProfit',     label: 'Net profit',       color: '#15803d', type: 'line', format: formatGbp },
  { key: 'jobsScheduled', label: 'Jobs (scheduled)', color: '#0f2942', type: 'line', format: (v) => String(v) },
  { key: 'jobsCompleted', label: 'Jobs (completed)', color: '#1e3a8a', type: 'line', format: (v) => String(v) },
  { key: 'jobsCancelled', label: 'Jobs (cancelled)', color: '#dc2626', type: 'line', format: (v) => String(v) },
  { key: 'spend',         label: 'Ad spend',         color: '#d97706', type: 'bar',  format: formatGbp },
  { key: 'leads',         label: 'Leads',            color: '#c9a961', type: 'line', format: (v) => String(v) },
  { key: 'adLeads',       label: 'Ad leads',         color: '#a16207', type: 'line', format: (v) => String(v) },
  { key: 'cpl',           label: 'CPL',              color: '#b91c1c', type: 'line', format: formatCpl },
];

interface RangePreset {
  label: string;
  range: () => { from: string; to: string };
}

const PRESETS: RangePreset[] = [
  { label: '7d',  range: () => ({ from: format(subDays(new Date(), 6),  'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: '30d', range: () => ({ from: format(subDays(new Date(), 29), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: '90d', range: () => ({ from: format(subDays(new Date(), 89), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: '6m',  range: () => ({ from: format(subMonths(new Date(), 6), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'YTD', range: () => ({ from: format(startOfYear(new Date()), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') }) },
];

export function CalendarPage() {
  const [from, setFrom] = useState(() => format(subDays(new Date(), 29), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [selected, setSelected] = useState<Set<MetricKey>>(new Set(['revenue', 'spend']));

  const { data, isLoading } = trpc.metrics.timeSeries.useQuery({ from, to, granularity, metrics: Array.from(selected) });

  const toggleMetric = (key: MetricKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next.size === 0 ? new Set(['revenue']) : next;
    });
  };

  const applyPreset = (p: RangePreset) => {
    const r = p.range();
    setFrom(r.from);
    setTo(r.to);
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.series.map((bk) => ({
      label: granularity === 'month' ? bk.bucket : bk.bucket.slice(5), // MM-DD or yyyy-MM
      revenue: Number(bk.revenue.toFixed(2)),
      jobsScheduled: bk.jobsScheduled,
      jobsCompleted: bk.jobsCompleted,
      jobsCancelled: bk.jobsCancelled,
      spend: Number(bk.spend.toFixed(2)),
      leads: bk.leads,
      adLeads: bk.adLeads,
      cpl: Number(bk.cpl.toFixed(2)),
      laborCost: Number(bk.laborCost.toFixed(2)),
      netProfit: Number(bk.netProfit.toFixed(2)),
    }));
  }, [data, granularity]);

  const visibleMetrics = METRICS.filter((m) => selected.has(m.key));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-navy">Calendar</h1>
        <p className="text-sm text-gray-500">Customisable time-series — pick range, granularity, metrics.</p>
      </div>

      {/* Controls */}
      <div className="card space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-gray-500">Range</span>
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => applyPreset(p)}
              className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-navy border-gray-200 hover:bg-cream-tint"
            >
              {p.label}
            </button>
          ))}
          <span className="text-xs text-gray-400 mx-2">or custom</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1 rounded border border-gray-200 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1 rounded border border-gray-200 text-sm" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-gray-500">Granularity</span>
          {(['day', 'week', 'month'] as const).map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-medium border',
                granularity === g ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200',
              )}
            >
              {g}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-gray-500 mr-2">Metrics</span>
          {METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => toggleMetric(m.key)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-2',
                selected.has(m.key) ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200',
              )}
            >
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
          <TotalsTile label="Revenue"      value={formatGbp(data.totals.revenue)} accent="green" />
          <TotalsTile label="Labor cost"   value={data.totals.laborCost > 0 ? formatGbp(data.totals.laborCost) : '—'} accent="red" />
          <TotalsTile label="Ad spend"     value={formatGbp(data.totals.spend)} accent="amber" />
          <TotalsTile label="Net profit"   value={formatGbp(data.totals.netProfit)} accent={data.totals.netProfit >= 0 ? 'green' : 'red'} />
          <TotalsTile label="Margin"       value={data.totals.revenue > 0 ? `${(data.totals.margin * 100).toFixed(1)}%` : '—'} accent="green" />
          <TotalsTile label="Jobs sched."  value={data.totals.jobsScheduled.toString()} accent="navy" />
          <TotalsTile label="Leads"        value={data.totals.leads.toString()} accent="gold" />
          <TotalsTile label="CPL"          value={data.totals.adLeads > 0 ? formatCpl(data.totals.cpl) : '—'} accent="amber" />
          <TotalsTile label="ROAS"         value={data.totals.roas > 0 ? `${data.totals.roas.toFixed(2)}×` : '—'} accent="green" />
        </div>
      )}

      {/* Main chart */}
      <div className="card">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !data || chartData.length === 0 ? (
          <p className="text-sm text-gray-500">No data in range.</p>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    const m = METRICS.find((x) => x.label === name);
                    return m ? m.format(value) : value;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {visibleMetrics.map((m) =>
                  m.type === 'bar' ? (
                    <Bar key={m.key} dataKey={m.key} name={m.label} fill={m.color} radius={[3, 3, 0, 0]} />
                  ) : (
                    <Line key={m.key} dataKey={m.key} name={m.label} stroke={m.color} strokeWidth={2} dot={{ r: 2 }} />
                  ),
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-bucket table — useful for scanning specific dates */}
      {data && chartData.length > 0 && chartData.length <= 100 && (
        <div className="card overflow-x-auto">
          <h2 className="font-display text-sm uppercase tracking-wider text-gray-500 mb-3">Per-bucket detail</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-500">
                <th className="text-left py-2 px-2 font-medium">{granularity === 'day' ? 'Date' : granularity === 'week' ? 'Week of' : 'Month'}</th>
                <th className="text-right py-2 px-2 font-medium font-mono">Revenue</th>
                <th className="text-right py-2 px-2 font-medium font-mono">Jobs</th>
                <th className="text-right py-2 px-2 font-medium font-mono">Cancelled</th>
                <th className="text-right py-2 px-2 font-medium font-mono">Spend</th>
                <th className="text-right py-2 px-2 font-medium font-mono">Leads</th>
                <th className="text-right py-2 px-2 font-medium font-mono">CPL</th>
                <th className="text-right py-2 px-2 font-medium font-mono">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((bk) => {
                const roas = bk.spend > 0 ? bk.revenue / bk.spend : 0;
                return (
                  <tr key={bk.label} className="border-b border-gray-50 hover:bg-cream-tint">
                    <td className="py-1.5 px-2 font-mono text-xs">{bk.label}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-green-700">{bk.revenue > 0 ? formatGbp(bk.revenue) : '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{bk.jobsScheduled || '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-red-600">{bk.jobsCancelled || '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{bk.spend > 0 ? formatGbp(bk.spend) : '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{bk.leads || '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{bk.adLeads > 0 ? formatCpl(bk.cpl) : '—'}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{roas > 0 ? `${roas.toFixed(2)}×` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && chartData.length > 100 && (
        <p className="text-xs text-gray-400 italic">Per-bucket table hidden when {`>`} 100 rows. Narrow the range or switch to a coarser granularity.</p>
      )}
    </div>
  );
}

function TotalsTile({ label, value, accent }: { label: string; value: string; accent: 'green' | 'navy' | 'red' | 'amber' | 'gold' }) {
  const border = {
    green: 'border-l-green-500',
    navy: 'border-l-navy',
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
    gold: 'border-l-gold',
  }[accent];
  return (
    <div className={clsx('card border-l-4', border)}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-display text-navy">{value}</p>
    </div>
  );
}
